/**
 * Hook para gestionar las columnas de AG Grid en el módulo OrderPreparation
 *
 * Responsabilidades:
 * - Definir las columnas (ColDef) con sus renderers personalizados
 * - Todas las columnas siempre visibles (sin selector de columnas)
 *
 * ⚠️ IMPORTANTE: NINGUNA columna debe ser sortable según requerimiento del usuario
 */
import { useMemo, type MouseEvent } from 'react';
import { ColDef } from 'ag-grid-community';
import { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { OrderPreparationModel } from '../models/OrderPreparation';
import { CustomColDef } from '@shared/components/ui/AgGridTable';
import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCartShopping, faEye } from '@fortawesome/pro-light-svg-icons';
import {
  getPreparationStatusColor,
  PreparationType,
  usePreparationStatusName,
  usePreparationTypeName,
} from '@modules/OrderPreparation/shared/config';
import { useNavigate } from 'react-router-dom';

// 🎯 Función utilitaria para formatear fechas como dd/mm/yyyy
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  } catch {
    return '-';
  }
};

// 🎯 Componente para la celda de estado
// eslint-disable-next-line react-refresh/only-export-components
const StatusCell = ({ status }: { status: number }) => {
  const statusName = usePreparationStatusName(status);
  const statusColor = getPreparationStatusColor(status);

  return (
    <Badge variant="outline" className={`text-xs bg-card-border border-0 ${statusColor}`}>
      {statusName}
    </Badge>
  );
};

// 🎯 Componente para la celda de tipo
// eslint-disable-next-line react-refresh/only-export-components
const TypeCell = ({ type }: { type: number }) => {
  const typeName = usePreparationTypeName(type);
  return <span className="text-sm font-medium">{typeName}</span>;
};

/**
 * Hook para gestionar las columnas de AG Grid
 *
 * ⚠️ CRÍTICO: NINGUNA columna tiene sortable: true según indicación del usuario
 * ⚠️ Patrón de Orders: Todas las columnas siempre visibles (sin ColumnSelector)
 */
export const useAgGridColumns = (selectedColumns: string[] = []) => {
  const { t } = useTranslation('orderpreparationModule');
  const navigate = useNavigate();

  // 🎯 Función para calcular la cantidad total de productos
  const getTotalQuantity = (lines: any[]) => {
    if (!lines || !Array.isArray(lines)) return 0;
    return lines.reduce((total, line) => total + (line.qty || 0), 0);
  };

  // 🎯 Definiciones completas de columnas para AG Grid
  // ⚠️ NINGUNA columna tiene sortable: true según requerimiento del usuario
  const allColumnDefs: ColDef[] = useMemo(() => {
    const baseColumns: CustomColDef[] = [
      // 🔵 COLUMNA: Código de preparación
      {
        field: 'code',
        headerName: t('table.code'),
        sortable: false, // ⚠️ NO SORTABLE según usuario
        // 🎯 CELDA: Código con formato
        cellRenderer: (params: any) => {
          const preparation = params.data as OrderPreparationModel;
          return <span className="text-sm font-medium text-title">{preparation.code}</span>;
        },
      },

      // 🔵 COLUMNA: Código de carrito
      {
        field: 'location',
        headerName: t('table.cart_code'),
        sortable: false, // ⚠️ NO SORTABLE según usuario
        // 🎯 CELDA: Código de carrito desde location.code
        cellRenderer: (params: any) => {
          const preparation = params.data as OrderPreparationModel;
          const cartCode = preparation.location?.code;
          return (
            <span className="text-sm font-medium text-title inline-flex items-center gap-1">
              {cartCode ? <FontAwesomeIcon icon={faCartShopping} className="w-3.5 h-3.5 text-icons" /> : null}
              {cartCode || '-'}
            </span>
          );
        },
      },

      // 🔵 COLUMNA: Estado
      {
        field: 'status',
        headerName: t('table.status'),
        sortable: false, // ⚠️ NO SORTABLE según usuario
        valueGetter: (params) => params.data.status,
        // 🎯 CELDA: Badge con color según estado
        cellRenderer: (params: any) => {
          const preparation = params.data as OrderPreparationModel;
          return <StatusCell status={preparation.status} />;
        },
      },

      // 🔵 COLUMNA: Fecha de creación
      {
        field: 'created_at',
        headerName: t('table.created_at'),
        sortable: false, // ⚠️ NO SORTABLE según usuario
        // 🎯 CELDA: Fecha formateada
        cellRenderer: (params: any) => {
          const preparation = params.data as OrderPreparationModel;
          return <span className="text-sm">{formatDate(preparation.created_at)}</span>;
        },
      },

      // 🔵 COLUMNA: Cantidad
      {
        field: 'quantity',
        headerName: t('table.quantity'),
        sortable: false, // ⚠️ NO SORTABLE según usuario
        // 🎯 CELDA: Badge circular con cantidad
        cellRenderer: (params: any) => {
          const preparation = params.data as OrderPreparationModel;
          const totalQty = getTotalQuantity(preparation.lines);
          return (
            <span className="inline-flex items-center justify-center rounded-full border border-lines text-xs font-medium text-title py-0.5 px-3">
              {totalQty}
            </span>
          );
        },
      },

      // 🔵 COLUMNA: Total de pedidos
      {
        field: 'total_orders',
        headerName: t('table.total_orders'),
        sortable: false, // ⚠️ NO SORTABLE según usuario
        // 🎯 CELDA: Badge circular con total de pedidos
        cellRenderer: (params: any) => {
          const preparation = params.data as OrderPreparationModel;
          const totalOrders = preparation.total_orders || 0;
          return (
            <span className="inline-flex items-center justify-center rounded-full border border-lines text-xs font-medium text-title py-0.5 px-3">
              {totalOrders}
            </span>
          );
        },
      },

      // 🔵 COLUMNA: Tipo de preparación
      {
        field: 'type',
        headerName: t('table.type'),
        sortable: false, // ⚠️ NO SORTABLE según usuario
        valueGetter: (params) => params.data.type,
        // 🎯 CELDA: Nombre del tipo
        cellRenderer: (params: any) => {
          const preparation = params.data as OrderPreparationModel;
          return <TypeCell type={preparation.type} />;
        },
      },
    ];

    // 🔵 COLUMNA: Acciones (siempre presente)
    baseColumns.push({
      headerName: '',
      field: 'actions',
      width: 80,
      minWidth: 80,
      maxWidth: 80,
      resizable: false,
      pinned: 'right',
      lockPosition: 'right', // Evita que el usuario mueva la columna
      suppressMovable: true, // Deshabilita el drag & drop para esta columna
      suppressSizeToFit: true,
      suppressAutoSize: true,
      sortable: false, // ⚠️ NO SORTABLE según usuario
      // 🎯 CELDA: Botón de ver detalles (Picking o Packing masivo según tipo). Ctrl/Cmd+click abre en nueva pestaña.
      cellRenderer: (params: any) => {
        const preparation = params.data as OrderPreparationModel;
        const type = preparation.type;
        const path =
          type === PreparationType.MASSIVE_PACKING
            ? `/order-preparation/massive-packing/${preparation.id}`
            : `/order-preparation/picking/${preparation.id}`;

        const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            window.open(path, '_blank', 'noopener,noreferrer');
          } else {
            navigate(path);
          }
        };

        const title = type === PreparationType.MASSIVE_PACKING ? 'Ver packing masivo' : 'Ver detalles';

        return (
          <div className="flex justify-center w-full">
            <Button variant="ghost" size="sm" onClick={handleClick} title={title} data-testid="view-details-button">
              <FontAwesomeIcon icon={faEye} className="w-4 h-4" />
            </Button>
          </div>
        );
      },
    });

    return baseColumns;
  }, [t, navigate]);

  // 🎯 CRÍTICO: Filtrar columnas basado en selectedColumns (sin ColumnSelector)
  const filteredColumnDefs: ColDef[] = useMemo(() => {
    // dYZ_ Si no hay selectedColumns, devolver todas las columnas
    if (selectedColumns.length === 0) {
      return allColumnDefs;
    }

    const alwaysVisibleFields = new Set(['actions']);

    // dYZ_ Filtrar columnas basado en selectedColumns, manteniendo las fijas
    return allColumnDefs.filter((col) => {
      const fieldId = col.field || col.headerName || '';
      if (!fieldId) {
        return false;
      }

      if (alwaysVisibleFields.has(fieldId)) {
        return true;
      }

      return selectedColumns.includes(fieldId) || selectedColumns.includes(col.headerName || '');
    });
  }, [allColumnDefs, selectedColumns]);

  // 🎯 Convertir a formato tanStack para ColumnSelector
  const tanStackColumns = useMemo<ColumnDef<OrderPreparationModel>[]>(() => {
    return allColumnDefs
      .filter((col) => {
        const fieldId = col.field || col.headerName || '';
        return fieldId && fieldId !== 'actions';
      })
      .map((col) => {
        const fieldId = col.field || col.headerName || '';
        return {
          id: fieldId,
          accessorKey: fieldId,
          header: col.headerName || fieldId,
        } as ColumnDef<OrderPreparationModel>;
      });
  }, [allColumnDefs]);

  // 🎯 Devolver columnas (todas siempre visibles)
  return {
    columnDefs: filteredColumnDefs,
    tanStackColumns,
  };
};
